import { cn, formatShares } from "@breakout/ui/utils"
import { TrendingDown, TrendingUp } from "lucide-react"
import { memo } from "react"
import type { OutcomeBucket } from "../../lib/types"
import { SimpleAvatar } from "../ui/SimpleAvatar"

interface OutcomeRowProps {
	bucket: OutcomeBucket
	userShares: number
	isSubmitting: boolean
	onBuy: (bucketId: string) => void
	onSell: (bucketId: string, shares: number) => void
	/** Whether trading is enabled (market is open and not ended) */
	isTradingEnabled?: boolean
	/** Compact: single line, Standard: two rows with larger touch targets */
	variant?: "compact" | "standard"
	/** Whether this is a Fall from Grace market (all outcomes show down arrow) */
	isFallFromGrace?: boolean
}

/**
 * Unified outcome row component for all market types
 * - compact: single-line layout for markets with many outcomes (Fall from Grace)
 * - standard: two-row layout with larger touch targets for binary/multi-outcome
 */
export const OutcomeRow = memo(function OutcomeRow({
	bucket,
	userShares,
	isSubmitting,
	onBuy,
	onSell,
	isTradingEnabled = true,
	variant = "standard",
	isFallFromGrace = false,
}: OutcomeRowProps) {
	const priceInCents = Math.round(bucket.probability * 100)
	const hasShares = userShares > 0
	// For FFG markets, all outcomes are "down" since it's betting on who falls most
	const isUp = !isFallFromGrace && bucket.direction === "up"
	const isDown = isFallFromGrace || bucket.direction === "down"

	if (variant === "compact") {
		return (
			<div className="flex items-center gap-2 px-3 py-2.5">
				{/* Avatar with arrow overlay — always reserve space to prevent layout shift */}
				<div className="relative size-8 shrink-0">
					{bucket.avatarUrl ? (
						<SimpleAvatar
							alt={bucket.label}
							className="size-8"
							size="sm"
							src={bucket.avatarUrl}
							variant="square"
						/>
					) : (
						<div className="size-8 animate-pulse rounded-lg bg-bg-tertiary" />
					)}
					{/* Arrow overlay */}
					<div
						className={cn(
							"absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full",
							isUp ? "bg-green-500" : isDown ? "bg-red-500" : "bg-gray-500",
						)}
					>
						{isUp ? (
							<TrendingUp aria-hidden="true" className="size-2.5 text-white" />
						) : isDown ? (
							<TrendingDown aria-hidden="true" className="size-2.5 text-white" />
						) : (
							<span className="font-bold text-[8px] text-white">~</span>
						)}
					</div>
				</div>

				{/* Name + shares */}
				<div className="min-w-0 flex-1">
					<span className="block truncate font-medium text-sm text-text-primary leading-tight">
						{bucket.label}
					</span>
					{hasShares && (
						<span className="mt-0.5 block text-[11px] text-breakout-brand leading-none">
							{formatShares(userShares)} shares
						</span>
					)}
				</div>

				{/* Price */}
				<span className="shrink-0 text-right font-bold text-text-primary tabular-nums">
					{priceInCents}¢
				</span>

				{/* Actions - always show both buttons to prevent layout flash */}
				<div className="flex shrink-0 gap-1">
					<button
						className="min-w-[42px] cursor-pointer rounded-lg bg-breakout-brand px-2.5 py-1.5 font-semibold text-black text-xs transition-colors hover:bg-breakout-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
						disabled={isSubmitting || !isTradingEnabled}
						onClick={() => onBuy(bucket.id)}
						type="button"
					>
						Buy
					</button>
					<button
						className={cn(
							"min-w-[42px] cursor-pointer rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 font-semibold text-red-600 text-xs transition-colors hover:border-red-300 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/25 dark:bg-red-500/15 dark:text-red-400 dark:hover:border-red-500/40 dark:hover:bg-red-500/25",
						)}
						disabled={isSubmitting || !hasShares || !isTradingEnabled}
						onClick={() => onSell(bucket.id, Math.floor(userShares))}
						type="button"
					>
						Sell
					</button>
				</div>
			</div>
		)
	}

	// Standard variant - two-row layout with larger touch targets
	return (
		<div className={cn("p-3 transition-colors", hasShares && "bg-breakout-brand/5")}>
			{/* Outcome info row */}
			<div className="mb-2 flex items-start justify-between">
				<div className="flex min-w-0 flex-1 items-center gap-2">
					{/* Avatar — always reserve space to prevent layout shift */}
					<div className="relative size-8 shrink-0">
						{bucket.avatarUrl ? (
							<SimpleAvatar
								alt={bucket.label}
								className="size-8"
								size="sm"
								src={bucket.avatarUrl}
								variant="square"
							/>
						) : (
							<div className="size-8 animate-pulse rounded-lg bg-bg-tertiary" />
						)}
						{/* Arrow overlay */}
						<div
							className={`absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full ${
								isUp ? "bg-green-500" : isDown ? "bg-red-500" : "bg-gray-500"
							}`}
						>
							{isUp ? (
								<TrendingUp aria-hidden="true" className="size-2.5 text-white" />
							) : isDown ? (
								<TrendingDown aria-hidden="true" className="size-2.5 text-white" />
							) : (
								<span className="font-bold text-[8px] text-white">~</span>
							)}
						</div>
					</div>
					<div className="min-w-0 flex-1">
						<p className="truncate font-semibold text-text-primary">{bucket.label}</p>
						{hasShares && (
							<p className="text-breakout-brand text-xs">{formatShares(userShares)} shares</p>
						)}
					</div>
				</div>
				<div className="text-right">
					<p className="font-bold text-2xl text-text-primary tabular-nums">{priceInCents}¢</p>
				</div>
			</div>

			{/* Action buttons row - always show both to prevent layout flash */}
			<div className="flex gap-2">
				<button
					className="min-h-[44px] flex-1 cursor-pointer rounded-xl bg-breakout-brand font-semibold text-black text-sm transition-colors hover:bg-breakout-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
					disabled={isSubmitting || !isTradingEnabled}
					onClick={() => onBuy(bucket.id)}
					type="button"
				>
					Buy {bucket.label} {priceInCents}¢
				</button>
				<button
					className="min-h-[44px] cursor-pointer rounded-xl border border-red-200 bg-red-50 px-4 font-semibold text-red-600 text-sm transition-colors hover:border-red-300 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/25 dark:bg-red-500/15 dark:text-red-400 dark:hover:border-red-500/40 dark:hover:bg-red-500/25"
					disabled={isSubmitting || !hasShares || !isTradingEnabled}
					onClick={() => onSell(bucket.id, Math.floor(userShares))}
					type="button"
				>
					Sell
				</button>
			</div>
		</div>
	)
})
